import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Bike, MapPin, UtensilsCrossed, Clock } from "lucide-react";
import api from "../lib/api";

/**
 * Page de confirmation de commande food après paiement Stripe.
 * Appelée par le success_url = /food/merci?order_id=xxx&session_id=cs_test_yyy
 * Redirige automatiquement vers la page suivi (avec carte) dès que le paiement est confirmé.
 */
export default function FoodOrderSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orderId = params.get("order_id");
  const sessionId = params.get("session_id");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orderId) { setLoading(false); return; }
    // Poll pendant 15s : le webhook Stripe met à jour le statut → "paid"
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 15;
    const check = async () => {
      try {
        const r = await api.get(`/food/order/${orderId}`);
        if (!cancelled) {
          setOrder(r.data);
          if (r.data?.status === "paid" || attempts >= maxAttempts) {
            setLoading(false);
            // Auto-redirection vers la page suivi (avec carte + livreur) après 2,5s
            // pour laisser au client le temps de voir le "Merci pour votre commande".
            if (r.data?.status === "paid" && r.data?.id) {
              setTimeout(() => {
                if (!cancelled) navigate(`/food/order/${r.data.id}`, { replace: true });
              }, 2500);
            }
            return;
          }
        }
      } catch { /* ignore */ }
      attempts++;
      if (!cancelled && attempts < maxAttempts) setTimeout(check, 1000);
      else if (!cancelled) setLoading(false);
    };
    check();
    return () => { cancelled = true; };
  }, [orderId, navigate]);

  return (
    <div className="min-h-screen bg-[#FDF8F0] flex items-center justify-center px-4 py-10">
      <div className="max-w-lg w-full bg-white rounded-3xl border-2 border-[#059669] p-8 sm:p-10 text-center shadow-xl">
        <div className="w-20 h-20 mx-auto rounded-full bg-[#059669] flex items-center justify-center mb-5">
          {loading ? (
            <Loader2 className="w-10 h-10 text-white animate-spin" />
          ) : (
            <CheckCircle2 className="w-12 h-12 text-white" />
          )}
        </div>
        <h1 className="font-head font-black text-2xl sm:text-3xl text-[#1A1A1A]">
          {loading ? "Traitement du paiement…" : "Merci pour votre commande !"}
        </h1>
        <p className="text-sm text-[#75807A] mt-2">
          {loading
            ? "Nous confirmons votre paiement avec Stripe. Cela prend quelques secondes."
            : order?.status === "paid"
              ? "Paiement confirmé. Redirection vers le suivi de votre livraison…"
              : "Votre paiement a été confirmé. Le restaurant prépare votre commande."}
        </p>

        {order && (
          <div className="mt-6 space-y-3 text-left bg-[#F0FDF4] rounded-2xl p-5 border border-[#059669]/20">
            <div className="flex items-start gap-3">
              <UtensilsCrossed className="w-5 h-5 text-[#059669] shrink-0 mt-0.5" />
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-[#059669]">Restaurant</div>
                <div className="font-bold text-[#1A1A1A]">{order.restaurant_name}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-[#059669] shrink-0 mt-0.5" />
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-[#059669]">Livraison à</div>
                <div className="font-bold text-[#1A1A1A]">{order.delivery_address}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Bike className="w-5 h-5 text-[#059669] shrink-0 mt-0.5" />
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-[#059669]">Total payé</div>
                <div className="font-bold text-[#1A1A1A]">{Number(order.total_eur || 0).toFixed(2)} €</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-[#059669] shrink-0 mt-0.5" />
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-[#059669]">Livraison estimée</div>
                <div className="font-bold text-[#1A1A1A]">Dans 30 minutes environ</div>
              </div>
            </div>
          </div>
        )}

        {/* PIN de signature — commandes à haute valeur (≥ 80 €) : le client
             donne ce code au livreur qui doit le saisir pour confirmer la livraison. */}
        {order?.signature_pin && (
          <div className="mt-4 bg-gradient-to-br from-[#F26419] to-[#D94D0E] text-white rounded-2xl p-5 text-center shadow-lg" data-testid="order-success-pin">
            <div className="text-[10px] uppercase tracking-widest font-black opacity-90">🔒 Code signature livraison</div>
            <div className="text-5xl font-black font-mono mt-2 tracking-widest tabular-nums">{order.signature_pin}</div>
            <p className="text-xs mt-3 opacity-95 leading-relaxed">
              Commande à <b>haute valeur</b> — donnez ce code à 4 chiffres au livreur au moment de la remise de la commande.
              Il ne pourra pas la marquer livrée sans lui.
            </p>
          </div>
        )}

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Link
            to="/"
            data-testid="order-success-home"
            className="flex-1 h-12 inline-flex items-center justify-center gap-2 rounded-full bg-[#059669] hover:bg-[#047857] text-white font-bold transition-colors"
          >
            <UtensilsCrossed className="w-4 h-4" /> Retour à l'accueil
          </Link>
          {order?.id && (
            <Link
              to={`/food/order/${order.id}`}
              data-testid="order-success-track"
              className="flex-1 h-12 inline-flex items-center justify-center gap-2 rounded-full bg-white border-2 border-[#059669] text-[#059669] hover:bg-[#F0FDF4] font-bold transition-colors"
            >
              Suivre ma commande
            </Link>
          )}
        </div>

        <p className="mt-6 text-[10px] text-[#75807A]">
          Un email de confirmation a été envoyé à votre adresse. Session Stripe : {sessionId ? sessionId.slice(0, 20) + "…" : "N/A"}
        </p>
      </div>
    </div>
  );
}
